import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Observable, Subscription, filter } from "rxjs";
import { AppState } from "src/app/store/app.reducer";
import * as DeviceVctActions from 'src/app/store/device-vct/device-vct.actions';
import { StandardPageLayout, StandardNewValidator } from "src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib";
import { selectDeviceVctFeatureEdited, selectDeviceVctFeatureEditing, selectDeviceVctModuleEdited, selectDeviceVctModuleEditing } from "src/app/store/device-vct/device-vct.selector";
import { FeatureVct, ModuleVct } from "src/app/model/device-vct/device-vct-implant";
import { DevicesVctFeatures } from "src/app/conf/devices-vct";

@Component({
	templateUrl: './edit-unit-vct.dialog.component.html',
})

export class EditUnitVctDialogComponent implements OnInit, OnDestroy {
	editDeviceForm: FormGroup;

	module: ModuleVct;
	iamUnitOwner: boolean;

	deviceVctModuleEditing: boolean;
	deviceVctModuleEditing$: Observable<boolean | undefined>;
	deviceVctModuleEditingSub: Subscription;
	deviceVctFeatureEditing: boolean;
	deviceVctFeatureEditing$: Observable<boolean | undefined>;
	deviceVctFeatureEditingSub: Subscription;

	deviceVctModuleEdited$: Observable<boolean | undefined>;
	deviceVctModuleEditedSub: Subscription;
	deviceVctFeatureEdited$: Observable<boolean | undefined>;
	deviceVctFeatureEditedSub: Subscription;

	featureListShow: FeatureVct[];

	loading: boolean = true;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private dialogRef: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private formBuilder: FormBuilder,
	) {
		if (config.data !== undefined && this.config.data.module !== undefined) {
			this.module = this.config.data.module;
			this.iamUnitOwner = this.config.data.iamUnitOwner;
		} else {
			this.module = undefined;
			this.iamUnitOwner = undefined;
		}
		this.initSelectors();
	}

	initSelectors(): void {
		this.deviceVctModuleEditing$ = this.store.select(selectDeviceVctModuleEditing);
		this.deviceVctFeatureEditing$ = this.store.select(selectDeviceVctFeatureEditing);
		this.deviceVctModuleEdited$ = this.store.select(selectDeviceVctModuleEdited);
		this.deviceVctFeatureEdited$ = this.store.select(selectDeviceVctFeatureEdited);
	}

	ngOnInit(): void {
		this.featureListShow = [];
		this.subscribeAll();
		if (this.module !== undefined) {
			this.buildForm();
		}
		this.onResize();

		StandardNewValidator.setFirstCompile(true);
	}

	ngOnDestroy(): void {
		this.deviceVctModuleEditingSub?.unsubscribe();
		this.deviceVctFeatureEditingSub?.unsubscribe();
		this.deviceVctModuleEditedSub?.unsubscribe();
		this.deviceVctFeatureEditedSub?.unsubscribe();
	}

	buildForm(): void {
		this.editDeviceForm = this.formBuilder.group(this.buildFormBuilder());
	}

	buildFormBuilder() {
		this.featureListShow = [];
		const res = {};
		let name = this.module.Name;
		if (this.module.AliasName !== '') {
			name = this.module.AliasName;
		}
		res['name'] = [name, [Validators.required,]];
		if (this.module.Features !== undefined && this.module.Features.length > 0 && this.iamUnitOwner) {
			this.module.Features.forEach((fea) => {
				if (fea.FeatureId === DevicesVctFeatures.InternalDevice) {
					let name = fea.Name;
					if (fea.AliasName !== '') {
						name = fea.AliasName;
					}
					res[fea.Id] = [name, [Validators.required]];
					this.featureListShow.push(fea);

				}
			});
		}
		this.loading = false;
		return res;
	}

	subscribeAll(): void {
		this.deviceVctModuleEditedSub = this.deviceVctModuleEdited$
			.pipe(filter(result => !!result))
			.subscribe(() => this.dialogRef.close('success')
		);
		this.deviceVctFeatureEditedSub = this.deviceVctFeatureEdited$
			.pipe(filter(result => !!result))
			.subscribe(() => this.dialogRef.close('success')
		);
		this.deviceVctModuleEditingSub = this.deviceVctModuleEditing$
			.subscribe((deviceVctModuleEditing) => {
				this.loading = deviceVctModuleEditing;
			}
		);
		this.deviceVctFeatureEditingSub = this.deviceVctFeatureEditing$
			.subscribe((deviceVctFeatureEditing) => {
				this.loading = deviceVctFeatureEditing;
			}
		);
	}

	get f(): any {
		return this.editDeviceForm.controls;
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.editDeviceForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.editDeviceForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save(): void {
		let newName = this.f.name.value;
		if (this.module.AliasName !== newName) {
			this.store.dispatch(DeviceVctActions.editVctModuleName({ moduleId: this.module.Id, aliasName: newName }));
		}
		if (this.module.Features !== undefined && this.module.Features.length > 0 && this.iamUnitOwner) {
			this.module.Features.forEach((fea) => {
				if (fea.FeatureId === DevicesVctFeatures.InternalDevice) {
					newName = this.f[fea.Id].value;
					if (fea.AliasName !== newName) {
						this.store.dispatch(DeviceVctActions.editVctFeatureName({ featureId: fea.Id, aliasName: newName }));
					}
				}
			});
		}
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	newShowErrors(type: EditUnitVctDialogComponent['f'], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: EditUnitVctDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

}